import {
  Bookmark,
  FolderCog,
  FolderOpen,
  HardDrive,
  Save,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { dirNameFromPath, fileNameFromPath } from "../lib/path-utils";
import { cn } from "../lib/utils";
import { getPlatformService } from "../platform";
import { useBookmarkStore } from "../store/bookmark-store";
import { useEditorStore } from "../store/editor-store";
import { showFolderPickerDialog } from "./dialogs";

function formatPathLabel(
  path: string,
  roots: Array<{ path: string; name?: string | null }>,
): string {
  const root = roots
    .filter((entry) => path === entry.path || path.startsWith(`${entry.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  if (!root) return path;
  const suffix = path === root.path ? "" : path.slice(root.path.length + 1);
  const rootName = root.name || fileNameFromPath(root.path) || root.path;
  return suffix ? `${rootName}/${suffix}` : rootName;
}

async function refreshWorkspaceFoldersFromEngine(): Promise<void> {
  const engine = getPlatformService().engine;
  const { folders } = await engine.workspaceListFolders();
  useEditorStore.setState({
    workspaceFolders: folders,
    workspacePath: folders[0]?.path ?? null,
  });
  if (folders.length > 0) {
    await useEditorStore.getState().refreshFileTree();
  } else {
    useEditorStore.setState({ fileTree: [] });
  }
}

function ActionButton({
  icon,
  label,
  onClick,
  disabled,
  tone = "default",
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  tone?: "default" | "danger";
}) {
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={disabled}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-[11px] font-medium transition-colors",
        disabled
          ? "cursor-not-allowed border-border/50 bg-muted/30 text-muted-foreground/50"
          : tone === "danger"
            ? "border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10"
            : "border-border bg-background text-foreground hover:bg-muted/60",
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function IconButton({
  icon,
  title,
  onClick,
  disabled,
  tone = "default",
}: {
  icon: React.ReactNode;
  title: string;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  tone?: "default" | "danger";
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={() => void onClick()}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors",
        disabled
          ? "cursor-not-allowed text-muted-foreground/30"
          : tone === "danger"
            ? "text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {icon}
    </button>
  );
}

function Section({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="min-w-0 space-y-2">
      <div className="flex h-7 items-center gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h2>
        {typeof count === "number" ? (
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
            {count}
          </span>
        ) : null}
        <div className="flex-1" />
        {action}
      </div>
      {children}
    </section>
  );
}

export function WorkspaceConfigPanel({ filePath }: { filePath?: string }) {
  const t = useT("editor");
  const currentWorkspaceFile = useEditorStore((s) => s.currentWorkspaceFile);
  const workspaceFolders = useEditorStore((s) => s.workspaceFolders);
  const { dirtyTabCount, editableTabCount } = useEditorStore(
    useShallow((s) => {
      let dirtyTabCount = 0;
      let editableTabCount = 0;
      for (const tab of s.tabs) {
        if (tab.isDirty) dirtyTabCount++;
        if (tab.kind !== "workspace-config" && tab.kind !== "document-doctor") {
          editableTabCount++;
        }
      }
      return { dirtyTabCount, editableTabCount };
    }),
  );
  const starred = useEditorStore((s) => s.starred);
  const addWorkspaceFolder = useEditorStore((s) => s.addWorkspaceFolder);
  const removeWorkspaceFolder = useEditorStore((s) => s.removeWorkspaceFolder);
  const closeWorkspace = useEditorStore((s) => s.closeWorkspace);
  const persistSessionState = useEditorStore((s) => s.persistSessionState);
  const setStatusMessage = useEditorStore((s) => s.setStatusMessage);
  const openFileFromPath = useEditorStore((s) => s.openFileFromPath);
  const unstarItem = useEditorStore((s) => s.unstarItem);
  const bookmarks = useBookmarkStore((s) => s.bookmarks);
  const removeBookmark = useBookmarkStore((s) => s.remove);
  const workspaceFile = filePath ?? currentWorkspaceFile;
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const workspaceDir = workspaceFile ? dirNameFromPath(workspaceFile) : "";
  const workspaceName = workspaceFile ? fileNameFromPath(workspaceFile) : t("workspace.notBound");

  const runAction = async (key: string, fn: () => Promise<void>) => {
    if (busyAction) return;
    setBusyAction(key);
    try {
      await fn();
    } finally {
      setBusyAction(null);
    }
  };

  const saveWorkspaceState = async () => {
    if (!workspaceFile) {
      await useEditorStore.getState().saveWorkspace();
      return;
    }
    await persistSessionState({ persist: true });
    setStatusMessage(t("workspace.saved"), 2000);
  };

  const handleAddFolder = async () => {
    const folderPath = await showFolderPickerDialog({ title: t("workspace.addRootDialog") });
    if (!folderPath) return;
    await runAction("add-folder", async () => {
      await addWorkspaceFolder(folderPath);
      await refreshWorkspaceFoldersFromEngine().catch(() => {});
      await persistSessionState({ persist: true });
      setStatusMessage(t("workspace.rootAdded", { name: fileNameFromPath(folderPath) }), 2000);
    });
  };

  const handleRemoveFolder = async (folderPath: string) => {
    await runAction(`remove-folder:${folderPath}`, async () => {
      if (workspaceFolders.length > 1) {
        await removeWorkspaceFolder(folderPath);
        await refreshWorkspaceFoldersFromEngine().catch(() => {});
        await persistSessionState({ persist: true });
        setStatusMessage(t("workspace.rootRemoved"), 2000);
        return;
      }
      await closeWorkspace();
    });
  };

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-background"
      data-testid="workspace-config-panel"
    >
      <div className="border-b border-border bg-muted/20 px-5 py-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary">
            <FolderCog size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-semibold text-foreground">
              {t("workspace.title")}
            </h1>
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
              <span className="truncate font-mono" title={workspaceFile ?? ""}>
                {workspaceName}
              </span>
              {workspaceDir ? (
                <span className="truncate font-mono text-muted-foreground/70">
                  {workspaceDir}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <ActionButton
              icon={<Save size={13} />}
              label={t("workspace.action.save")}
              onClick={() => runAction("save", saveWorkspaceState)}
              disabled={busyAction !== null}
            />
            <ActionButton
              icon={<X size={13} />}
              label={t("workspace.action.close")}
              tone="danger"
              onClick={() => runAction("close", closeWorkspace)}
              disabled={workspaceFolders.length === 0 || busyAction !== null}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-5 py-5">
        <div className="mx-auto w-full max-w-5xl space-y-6">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
            <Stat label={t("workspace.stats.folders")} value={workspaceFolders.length} />
            <Stat label={t("workspace.stats.openFiles")} value={editableTabCount} />
            <Stat label={t("workspace.stats.dirty")} value={dirtyTabCount} />
            <Stat label={t("workspace.stats.bookmarks")} value={bookmarks.length} />
            <Stat label={t("workspace.stats.starred")} value={starred.length} />
          </div>

          <Section
            title={t("workspace.stats.folders")}
            count={workspaceFolders.length}
            action={
              <ActionButton
                icon={<FolderOpen size={13} />}
                label={t("workspace.action.addRoot")}
                onClick={handleAddFolder}
                disabled={busyAction !== null}
              />
            }
          >
            <div className="overflow-hidden rounded-lg border border-border">
              {workspaceFolders.length === 0 ? (
                <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
                  {t("workspace.empty.folders")}
                </div>
              ) : (
                workspaceFolders.map((folder) => (
                  <div
                    key={folder.path}
                    className="flex min-w-0 items-center gap-3 border-b border-border px-3 py-2 last:border-b-0"
                  >
                    <HardDrive size={15} className="shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12px] font-medium text-foreground">
                        {folder.name || fileNameFromPath(folder.path)}
                      </div>
                      <div className="truncate font-mono text-[10px] text-muted-foreground">
                        {folder.path}
                      </div>
                    </div>
                    <IconButton
                      icon={<Trash2 size={13} />}
                      title={workspaceFolders.length > 1 ? t("workspace.action.removeFromWorkspace") : t("workspace.action.close")}
                      tone="danger"
                      disabled={busyAction !== null}
                      onClick={() => handleRemoveFolder(folder.path)}
                    />
                  </div>
                ))
              )}
            </div>
          </Section>

          <Section title={t("workspace.bookmarks")} count={bookmarks.length}>
            <div className="overflow-hidden rounded-lg border border-border">
              {bookmarks.length === 0 ? (
                <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
                  {t("workspace.empty.bookmarks")}
                </div>
              ) : (
                bookmarks.map((item) => (
                  <div
                    key={item.id}
                    className="flex min-w-0 items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
                  >
                    <Bookmark size={14} className="shrink-0 text-muted-foreground" />
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => void openFileFromPath(item.path)}
                      title={`${item.path}:${item.line}`}
                    >
                      <div className="truncate text-[12px] font-medium text-foreground">
                        {item.label || `${fileNameFromPath(item.path)}:${item.line}`}
                      </div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {formatPathLabel(item.path, workspaceFolders)}
                      </div>
                    </button>
                    <IconButton
                      icon={<Trash2 size={13} />}
                      title={t("workspace.action.removeBookmark")}
                      tone="danger"
                      onClick={async () => {
                        await removeBookmark(item.id);
                      }}
                    />
                  </div>
                ))
              )}
            </div>
          </Section>

          <Section title={t("workspace.starred")} count={starred.length}>
            <div className="overflow-hidden rounded-lg border border-border">
              {starred.length === 0 ? (
                <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
                  {t("workspace.empty.starred")}
                </div>
              ) : (
                starred.map((item) => (
                  <div
                    key={item.path}
                    className="flex min-w-0 items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
                  >
                    <Star size={14} className="shrink-0 text-primary" />
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => {
                        if (item.kind === "folder") {
                          useEditorStore.setState({
                            sidebarOpen: true,
                            sidebarView: "explorer",
                          });
                          void useEditorStore.getState().expandTreeNode(item.path);
                        } else {
                          void openFileFromPath(item.path);
                        }
                      }}
                      title={item.path}
                    >
                      <div className="truncate text-[12px] font-medium text-foreground">
                        {item.label || fileNameFromPath(item.path)}
                      </div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {formatPathLabel(item.path, workspaceFolders)}
                      </div>
                    </button>
                    <IconButton
                      icon={<X size={13} />}
                      title={t("workspace.action.unstar")}
                      tone="danger"
                      onClick={() => unstarItem(item.path)}
                    />
                  </div>
                ))
              )}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums text-foreground">
        {value}
      </div>
    </div>
  );
}
